import type {
  SubmitPublicLeadRequest,
  LeadConfirmation,
  PhotoUploadResponse,
  PhotoScanStatus,
  ActiveProduct,
} from "@greatway/api-client";
import { API_BASE } from "./config";

/**
 * Public (unauthenticated) API client for the F1.5-2 capture flow. Unlike the admin portal's
 * client, these calls attach NO Authorization header — every endpoint is [AllowAnonymous].
 * The photo endpoints are token-gated via the X-Submission-Token header (the opaque session
 * token returned by submit, carried in the confirmation URL fragment).
 */

export class ApiError extends Error {
  constructor(
    public status: number,
    message: string,
    public problem?: { type?: string; title?: string; detail?: string; errors?: Record<string, string[]> },
  ) {
    super(message);
    this.name = "ApiError";
  }
}

async function readError(res: Response): Promise<ApiError> {
  const problem = await res.json().catch(() => null);
  return new ApiError(res.status, problem?.detail ?? res.statusText, problem ?? undefined);
}

const v1 = (path: string) => `${API_BASE}/api/v1${path}`;

/** Submit a public enquiry. Returns the generic confirmation (token populated only on a genuine create). */
export async function submitPublicLead(body: SubmitPublicLeadRequest): Promise<LeadConfirmation> {
  const res = await fetch(v1("/public/leads"), {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(body),
  });
  if (!res.ok) throw await readError(res);
  return res.json() as Promise<LeadConfirmation>;
}

/** Upload up to 10 photos for a submission (multipart). Token gates the upload via the X-Submission-Token header. */
export async function uploadPhotos(token: string, files: File[]): Promise<PhotoUploadResponse> {
  const form = new FormData();
  for (const file of files) form.append("files", file, file.name);
  const res = await fetch(v1("/public/leads/photos"), {
    method: "POST",
    headers: { "X-Submission-Token": token },
    body: form,
  });
  if (!res.ok) throw await readError(res);
  return res.json() as Promise<PhotoUploadResponse>;
}

/** Poll the AV-scan status of an uploaded photo (token-gated). */
export async function getPhotoStatus(token: string, photoId: number): Promise<PhotoScanStatus> {
  const res = await fetch(v1(`/public/leads/photos/${photoId}/status`), {
    headers: { "X-Submission-Token": token },
  });
  if (!res.ok) throw await readError(res);
  return res.json() as Promise<PhotoScanStatus>;
}

/**
 * Active product catalogue for the product-interest dropdown. Best-effort: if the endpoint
 * isn't anonymously reachable (spec §14 Q8 open), the form falls back to a free-text input.
 */
export async function getActiveProducts(): Promise<ActiveProduct[]> {
  const res = await fetch(v1("/products/active"));
  if (!res.ok) throw await readError(res);
  return res.json() as Promise<ActiveProduct[]>;
}
